import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncMultiSelectDropDown, Button, TabBar, TabItem, TextInput } from '@wot/components';
import { useClickOutside } from '@wot/shared';
import { useEffect, useRef, useState } from 'react';
import classesApi from '../../../api/classes';
import { SPINNER } from '../../../assets/animated';
import { FILTER_SVG } from '../../../assets/icons';
import NoResults from '../../../components/NoResults';
import Pagination from '../../../components/Pagination';
import usePagainate from '../../../hooks/usePaginate';
import { useTab } from '../../../hooks/useTab';
import TableContainer from './components/Table';

interface Filters {
  categoryType: string,
  category: string[],
  id: string,
  tutorAttendance: string,
  studentAttendance: string
}

export type Sorting = 'createdAt' | 'name' | 'scheduledTime'

export default function ClassManagement() {
  const queryClient = useQueryClient();
  const { activeTab, onTabChange } = useTab('all');
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ "createdAt": -1 });
  const [filters, setFilters] = useState<Filters | {}>({});

  const handleSort = (key: Sorting) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  useEffect(() => {
    resetPage();
  }, [activeTab]);

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['classes', currentPage, resultsPerPage, activeTab, sortBy, filters],
    queryFn: async () =>
      await classesApi.getClasses(
        currentPage,
        resultsPerPage,
        activeTab === 'all'
          ? { ...filters }
          : activeTab === 'action_required'
            ? { actionRequired: 'mark-attendance,schedule', ...filters }
            : { status: activeTab, ...filters },
        { ...sortBy },
      )
  });

  useEffect(() => {
    if (!isPreviousData) {
      queryClient.prefetchQuery({
        queryKey: ['classes', currentPage + 1],
        queryFn: async () =>
          await classesApi.getClasses(
            currentPage,
            resultsPerPage,
            activeTab === 'all'
              ? {}
              : activeTab === 'action_required'
                ? { actionRequired: 'mark-attendance,schedule', ...filters }
                : { status: activeTab, sortBy, ...filters },
            { ...sortBy }
          )
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError]);

  const filterResults = (filters: any) => {
    setFilters(filters);
  }

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Class Management</h1>
      <TabBar value={activeTab} onChange={onTabChange} textColor="text-black" indicatorColor="bg-primary">
        <TabItem label="All" value="all" />
        <TabItem label="Action Required" value="action_required" />
        <TabItem label="Upcoming" value="upcoming" />
        <TabItem label="Completed" value="completed" />
        <TabItem label="Cancelled" value="cancelled" />
      </TabBar>
      <Filters onApply={filterResults} onClear={() => setFilters({})} filters={filters} />
      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data.data.length > 0 ? (
        <>
          {activeTab === 'action_required' ? (
            <TableContainer handleSort={handleSort} data={data.data} actionRequired={true} />
          ) : (
            <TableContainer handleSort={handleSort} data={data.data} />
          )}
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  );
}
interface Props {
  onApply: (filters: any) => void
  onClear: () => void,
  filters: Filters | {}
}
function Filters({ filters, onApply, onClear }: Props) {
  const [showOptions, setShowOptions] = useState(false);
  const filterSortRef = useRef(null);

  const [temp, setTemp] = useState<Filters>({
    category: [],
    categoryType: "",
    id: "",
    studentAttendance: "",
    tutorAttendance: ""
  });

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      // @ts-ignore
      setTemp(filters);
    }
  }, [filters]);

  useClickOutside(filterSortRef, () => setShowOptions(false));

  return (
    <div className='relative flex justify-end'>
      <div onClick={() => setShowOptions(true)} className={`w-7 h-7 ${Object.keys(filters).length > 0 ? "bg-blue-100" : "bg-white"} flex items-center justify-center cursor-pointer border border-gray-300 rounded-md p-1 drop-shadow-sm`}>
        <img src={FILTER_SVG} />
      </div>
      {showOptions &&
        <div
          ref={filterSortRef}
          className="md:min-w-[500px] min-w-full flex flex-col border border-neutralBlue1 absolute top-14 md:right-5 right-0 p-6 bg-white drop-shadow-lg rounded-lg z-40"
        >
          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Category</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, categoryType: "" })}
              >
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 sm:gap-0 justify-between">
              <div
                className={`${temp.categoryType === 'subject' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, categoryType: "subject" })}
              >
                Subject
              </div>
              <div
                className={`${temp.categoryType === 'hobby' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, categoryType: "hobby" })}
              >
                Hobby
              </div>
              <div
                className={`${temp.categoryType === 'skill' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, categoryType: "skill" })}
              >
                Skill Development
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <div className="flex flex-row gap-3">
                <p className="text-sm font-medium">Filter By Category Name</p>
                <div
                  className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                  onClick={() => setTemp({ ...temp, category: [] })}>
                  Clear
                </div>
              </div>
            </div>
            <AsyncMultiSelectDropDown
              callback={async () => classesApi.getFacetsByCategoryName().then((data) => {
                return data[0].categorizebyCategoryName.filter((i: any) => i._id !== '').map((i: any) => i._id);
              })}
              onChange={(e) => setTemp({ ...temp, category: e })}
              placeholder='Enter Category Name'
              values={temp.category}
            />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <div className="flex flex-row gap-3">
                <p className="text-sm font-medium">Filter By Class Id</p>
                <div
                  className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                  onClick={() => setTemp({ ...temp, id: "" })}>
                  Clear
                </div>
              </div>
            </div>
            <TextInput
              placeholder={"Enter Class Id"}
              onChange={(e: string) => setTemp({ ...temp, id: e })}
              value={temp.id}
              type='text'
            />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Tutor's Attendace</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, tutorAttendance: "" })}>
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 justify-between">
              <div
                className={`${temp.tutorAttendance === 'absent' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, tutorAttendance: "absent" })}>
                Absent
              </div>
              <div
                className={`${temp.tutorAttendance === 'present' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, tutorAttendance: "present" })}>
                Present
              </div>
              <div
                className={`${temp.tutorAttendance === 'pending' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full  border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, tutorAttendance: "pending" })}>
                Pending
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Students's Attendace</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, studentAttendance: "" })}>
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 justify-between">
              <div
                className={`${temp.studentAttendance === 'absent' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, studentAttendance: "absent" })}>
                Absent
              </div>
              <div
                className={`${temp.studentAttendance === 'present' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full  border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, studentAttendance: "present" })}>
                Present
              </div>
              <div
                className={`${temp.studentAttendance === 'pending' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full  border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, studentAttendance: "pending" })}>
                Pending
              </div>
            </div>
          </div>

          <div className="flex mt-5 gap-7">
            <Button
              text="Clear All"
              block
              type="s-outlined"
              onClick={() => {
                onClear();
              }}
            />
            <Button
              text="Apply"
              block
              type="primary"
              onClick={() => {
                onApply(temp)
                setShowOptions(false);
              }}
            />
          </div>
        </div>
      }
    </div >
  )
}